import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import styled, {useTheme} from 'styled-components';
import dayjs from 'dayjs';
import {observer} from 'mobx-react-lite';
import {useLocation, useNavigate, useParams} from 'react-router';
import {
  useQueries,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from 'react-query';
import {Button} from '@ui/atoms/button';
import {Avatar} from '@ui/atoms/avatar';
import {Body1, Body2, Headline2} from '@ui/atoms/typography';
import {CancelIcon, ChevronDownIcon} from '@ui/atoms/icons';
import {MoreVerticalIcon2} from '@ui/atoms/icons/more-vertical';
import {SearchBox2} from '@ui/molecules/search-box';
import {DropdownVertical} from '@ui/molecules/dropdown-vertical';
import {Modal} from '@ui/molecules/modal';
import {BackAction} from '@ui/molecules/back-action';
import {VerticalSpacer} from '@ui/atoms/spacer';
import {ModalCard} from '@ui/layouts/modal-card';
import {ConfirmationDialog} from '@ui/molecules/confirmation-dialog';
import {ItemLoader} from '@ui/organisms/item-loader';
import PageLayoutTemplate from '@ui/templates/page-template/page-layout';
import {useStoreContext} from '@store/store-context';
import {useDropDown} from '@hooks';
import {DropdownItem} from '@pages/dashboard/you/you-page.styles';
import {CompanyCheckinsController} from '@pages/dashboard/company-legacy/company-components/company-checkins/company-checkins.controller';
import {CompanyCheckinsRequestImpl} from '@pages/dashboard/company-legacy/company-components/company-checkins/company-checkins.request';
import {
  ICheckinCycleDetail,
  ISpecificCheckinsWithResponse,
  ISpecificCheckinsWithResponseData,
} from '@pages/dashboard/company-legacy/company-components/company-checkins/company-checkins.interface';
import {groupTypeController} from '@pages/dashboard/admin/admin-components/admin-people/admin-group-type/admin-group-type-controller';
import {groupTypeRequestImpl} from '@pages/dashboard/admin/admin-components/admin-people/admin-group-type/admin-group-type-request';
import {groupController} from '@pages/dashboard/admin/admin-components/admin-people/admin-group/admin-group-controller';
import {groupRequestImpl} from '@pages/dashboard/admin/admin-components/admin-people/admin-group/admin-group-request';
import {PreviewParticipantsCard} from '../create-checkin-cycle/components/preview-participants-card';
import {PreviewContentCard} from '../create-checkin-cycle/components/preview-content-card';
import {PreviewCadenceCard} from '../create-checkin-cycle/components/preview-cadence-card';
import {formatCadenceDetails} from '../create-checkin-cycle/components/preview-step';
import {buildPayload} from '../create-checkin-cycle/components/cycle-payload';
import {SelectOption} from '../create-checkin-cycle/components/types';
import {
  buildBlocksFromCycle,
  mapCycleToFormData,
} from '../create-checkin-cycle/create-checkin-cycle';
import {CycleEditSidecar} from './cycle-edit-sidecar';
import {EditableCycleSection} from '../create-checkin-cycle/components/cycle-form-validation';
import {
  attachCycleEnrichment,
  buildOccurrenceGroups,
  CycleOccurrence,
  getAdminSubmissionFilter,
  getEffectiveCycleStatus,
  getParticipationStats,
  markSubmissionExcusedInList,
  mergeAudienceWithOccurrence,
  resolveCycleAudience,
  getSubmissionRows,
  getSubmissionCycleId,
  getSubmittedAt,
  getCheckinSubmissionStatus,
  checkinSubmissionStatusLabel,
  isSubmissionSubmitted,
  ManagedCycle,
  ManagedSubmission,
  ManagedUser,
  withoutPendingInvitationSubmissions,
} from './team-management.utils';
import {canAdministerCheckins} from '../checkins-access';
import {AnonymousPulseSummary} from '../shared/anonymous-pulse-summary';
import {
  buildAnonymousPulseSummary,
  MIN_ANONYMOUS_PULSE_ANSWERS,
} from '../shared/anonymous-pulse-summary.utils';
import {formatSendOptionLabel} from '../my_checkins/team-feed.utils';
import {
  getReminderCooldownStorageKey,
  getReminderSecondsRemaining,
  readReminderCooldowns,
  REMINDER_COOLDOWN_MS,
  ReminderCooldowns,
  writeReminderCooldowns,
} from './reminder-cooldown';

const Page = styled.main`
  display: flex;
  width: 100%;
  max-width: 894px;
  margin: 0 auto;
  padding-bottom: 48px;
  flex-direction: column;
`;

const CycleDetailCard = styled.section`
  display: flex;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  flex-direction: column;
  gap: 24px;
  border: 1px solid ${({theme}) => theme.colors.borderLight};
  border-radius: 10px;
  background: ${({theme}) => theme.colors.white};
  padding: 24px;

  @media (max-width: 640px) {
    padding: 16px;
  }
`;

const CycleHeader = styled.section`
  display: flex;
  box-sizing: border-box;
  align-items: flex-end;
  justify-content: space-between;
  gap: 20px;

  @media (max-width: 640px) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

const CycleIdentity = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;
`;

const CycleName = styled.h1`
  min-width: 0;
  overflow: hidden;
  color: ${({theme}) => theme.colors.blackTwo};
  font-size: 24px;
  font-weight: 600;
  line-height: 32px;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const CycleActionButton = styled(Button)`
  width: auto;
  height: 38px;
  box-sizing: border-box;
  border-color: ${({theme}) => theme.colors.borderDark};
  border-radius: 8px;
  background: ${({theme}) => theme.colors.white};
  padding: 8px 16px;
  color: ${({theme}) => theme.colors.blackTwo};
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
`;

const CycleStatus = styled.span<{$active: boolean}>`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: ${({theme}) => theme.colors.textDark};
  font-size: 14px;
  font-weight: 600;
  line-height: 22px;
  text-transform: capitalize;
  white-space: nowrap;

  &:before {
    width: 10px;
    height: 10px;
    flex: 0 0 10px;
    border-radius: 50%;
    background: ${({$active, theme}) =>
      $active ? theme.colors.green300 : theme.colors.textMuted};
    content: '';
  }
`;

const Tabs = styled.div`
  display: flex;
  gap: 25px;
  border-bottom: 1px solid ${({theme}) => theme.colors.borderLight};
`;

const Tab = styled.button<{$active: boolean}>`
  border: 0;
  border-bottom: 2px solid
    ${({$active, theme}) => ($active ? theme.colors.purple300 : 'transparent')};
  background: none;
  padding: 0 0 16px;
  color: ${({$active, theme}) =>
    $active ? theme.colors.blackTwo : theme.colors.textBody};
  font-size: 14px;
  font-weight: ${({$active}) => ($active ? 600 : 500)};
  line-height: 22px;
`;

const ResponsesContent = styled.section`
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 24px;
`;

const SendCycleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  @media (max-width: 520px) {
    align-items: stretch;
    flex-direction: column;
    gap: 8px;
  }
`;

const OccurrencePicker = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  color: ${({theme}) => theme.colors.textDark};
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;

  @media (max-width: 520px) {
    align-items: stretch;
    flex-direction: column;
    gap: 8px;

    width: 100%;
  }
`;

const OccurrenceControl = styled.div`
  position: relative;
  width: 306px;
  min-width: 0;

  @media (max-width: 520px) {
    width: 100%;
  }
`;

const OccurrenceTrigger = styled.button`
  display: flex;
  width: 100%;
  height: 38px;
  min-width: 0;
  box-sizing: border-box;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border: 1px solid ${({theme}) => theme.colors.borderDark};
  border-radius: 10px;
  background: ${({theme}) => theme.colors.white};
  padding: 0 16px;
  color: ${({theme}) => theme.colors.blackTwo};
  font-size: 14px;
  font-weight: 600;
  line-height: 22px;
  text-align: left;

  &:disabled {
    cursor: not-allowed;
    background: ${({theme}) => theme.colors.backgroundDark};
  }
`;

const OccurrenceTriggerContent = styled.span`
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 12px;
`;

const OccurrenceLabel = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const OccurrenceDot = styled.span`
  width: 8px;
  height: 8px;
  flex: 0 0 8px;
  border-radius: 50%;
  background: ${({theme}) => theme.colors.green400};
`;

const OccurrenceChevron = styled.span`
  display: grid;
  width: 16px;
  height: 16px;
  flex: 0 0 16px;
  place-items: center;
`;

const OccurrenceMenu = styled.div`
  position: absolute;
  z-index: 10;
  top: calc(100% + 8px);
  left: 0;
  width: 100%;
  box-sizing: border-box;
  border: 1px solid ${({theme}) => theme.colors.borderLight};
  border-radius: 10px;
  background: ${({theme}) => theme.colors.white};
  padding: 8px;
  box-shadow: 0 10px 15px rgba(30, 30, 47, 0.12);
`;

const OccurrenceOptions = styled.div`
  max-height: 232px;
  overflow-y: auto;
  margin-top: 16px;
`;

const NoOccurrenceMatch = styled(Body2)`
  padding: 8px 16px;
`;

const ParticipationSummary = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
  border: 1px solid ${({theme}) => theme.colors.borderLight};
  border-radius: 10px;
  background: ${({theme}) => theme.colors.white};
  padding: 12px 16px;
  box-shadow: 0 3px 12px rgba(208, 208, 221, 0.16);
`;

const ParticipationHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;

  @media (max-width: 560px) {
    align-items: stretch;
    flex-direction: column;
  }
`;

const ParticipationCopy = styled.div`
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 2px;

  strong {
    color: ${({theme}) => theme.colors.blackTwo};
    font-size: 20px;
    font-weight: 600;
    line-height: 28px;
  }

  span {
    color: ${({theme}) => theme.colors.textBody};
    font-size: 12px;
    font-weight: 400;
    line-height: 20px;
  }
`;

const BulkReminderButton = styled(Button)`
  width: auto;
  min-width: 192px;
  height: 38px;
  box-sizing: border-box;
  border-radius: 10px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 600;
  line-height: 22px;

  @media (max-width: 560px) {
    width: 100%;
  }
`;

const ProgressTrack = styled.div`
  width: 100%;
  height: 10px;
  overflow: hidden;
  border-radius: 8px;
  background: ${({theme}) => theme.colors.borderLight};
`;

const ProgressValue = styled.div`
  height: 100%;
  border-radius: 8px;
  background: ${({theme}) => theme.colors.purple300};
  transition: width 200ms ease;
`;

const ParticipantsCard = styled.section`
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 16px;
  border: 1px solid ${({theme}) => theme.colors.borderLight};
  border-radius: 10px;
  background: ${({theme}) => theme.colors.white};
  padding: 17px;
`;

const ParticipantsToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  @media (max-width: 680px) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

const Filters = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Filter = styled.button<{$active: boolean}>`
  height: 36px;
  box-sizing: border-box;
  border: 1px solid
    ${({$active, theme}) =>
      $active ? theme.colors.purple300 : theme.colors.borderDark};
  border-radius: 8px;
  background: ${({$active, theme}) =>
    $active ? theme.colors.purple300 : theme.colors.white};
  padding: 8px 16px;
  color: ${({$active, theme}) =>
    $active ? theme.colors.alwaysWhite : theme.colors.textBody};
  font-size: 12px;
  font-weight: ${({$active}) => ($active ? 600 : 400)};
  line-height: 20px;
`;

const SortLabel = styled.span`
  color: ${({theme}) => theme.colors.textBody};
  font-size: 12px;
  font-weight: 400;
  line-height: 20px;
  white-space: nowrap;
`;

const Participants = styled.div`
  min-width: 0;
`;

const Participant = styled.div<{
  $position: 'first' | 'middle' | 'last' | 'only';
}>`
  display: grid;
  grid-template-columns:
    minmax(200px, 1fr) minmax(170px, 0.8fr) 140px
    minmax(150px, 0.85fr);
  align-items: center;
  gap: 8px;
  min-height: 76px;
  box-sizing: border-box;
  border: 1px solid ${({theme}) => theme.colors.borderLight};
  border-radius: ${({$position}) =>
    $position === 'only'
      ? '10px'
      : $position === 'first'
      ? '10px 10px 0 0'
      : $position === 'last'
      ? '0 0 10px 10px'
      : '0'};
  background: ${({theme}) => theme.colors.white};
  padding: 16px;

  & + & {
    margin-top: -1px;
  }

  @media (max-width: 1000px) {
    grid-template-columns: minmax(0, 1fr) auto;
    row-gap: 6px;
  }

  @media (max-width: 650px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const Person = styled.div`
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 10px;
`;

const PersonDetails = styled.div`
  min-width: 0;

  strong,
  span {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  strong {
    color: ${({theme}) => theme.colors.textDark};
    font-size: 14px;
    font-weight: 600;
    line-height: 22px;
  }

  span {
    color: ${({theme}) => theme.colors.textBody};
    font-size: 12px;
    font-weight: 400;
    line-height: 20px;
  }
`;

const Timestamp = styled.div`
  color: ${({theme}) => theme.colors.textBody};
  font-size: 12px;
  font-weight: 400;
  line-height: 20px;
  text-align: center;
  white-space: nowrap;

  @media (max-width: 1000px) {
    grid-column: 1;
    grid-row: 2;
    text-align: left;
  }
`;

const ParticipantStatus = styled.span<{
  $tone: 'submitted' | 'pending' | 'muted';
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: ${({theme}) => theme.colors.textDark};
  font-size: 12px;
  font-weight: 400;
  line-height: 20px;
  white-space: nowrap;

  &:before {
    width: 10px;
    height: 10px;
    flex: 0 0 10px;
    border-radius: 50%;
    background: ${({$tone, theme}) =>
      $tone === 'submitted'
        ? theme.colors.green300
        : $tone === 'pending'
        ? theme.colors.red300
        : theme.colors.textMuted};
    content: '';
  }

  @media (max-width: 1000px) {
    grid-column: 1;
    grid-row: 3;
    justify-content: flex-start;
  }
`;

const ActionCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;

  @media (max-width: 1000px) {
    grid-column: 2;
    grid-row: 1 / span 3;
  }

  @media (max-width: 650px) {
    grid-column: 1;
    grid-row: 4;
    justify-content: flex-start;
  }
`;

const ReminderButton = styled(Button)`
  min-width: 82px;
  flex-shrink: 0;
  height: 38px;
  box-sizing: border-box;
  border-radius: 10px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 600;
  line-height: 22px;
  white-space: nowrap;

  &[data-cooling='true'] {
    min-width: 178px;
  }
`;

const ViewButton = styled(Button)`
  height: 38px;
  box-sizing: border-box;
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 600;
  line-height: 22px;
`;

const ExcuseMenu = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ExcuseMenuItem = styled(DropdownItem)`
  display: flex;
  min-height: 40px;
  align-items: center;
  padding: 8px 12px;
  color: ${({theme}) => theme.colors.textBody};
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  text-align: left;
`;

const ParticipantsEmpty = styled.div`
  border: 1px solid ${({theme}) => theme.colors.borderLight};
  border-radius: 10px;
  padding: 40px 20px;
  color: ${({theme}) => theme.colors.textBody};
  text-align: center;
`;

const Settings = styled.div`
  display: flex;
  width: 100%;
  min-width: 0;
  flex-direction: column;
  gap: 24px;
`;

const CycleActions = styled.section`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  border: 1px dashed ${({theme}) => theme.colors.red400};
  border-radius: 10px;
  background: ${({theme}) => theme.colors.white};
  padding: 20px;

  @media (max-width: 560px) {
    align-items: stretch;
    flex-direction: column;
  }
`;

const Empty = styled.div`
  padding: 40px 20px;
  color: ${({theme}) => theme.colors.textBody};
  text-align: center;
`;

type DetailTab = 'responses' | 'settings';
type ResponseFilter = 'all' | 'submitted' | 'pending';

interface OccurrenceDropdownProps {
  occurrences: CycleOccurrence[];
  selectedOccurrence?: CycleOccurrence;
  onSelect: (key: string) => void;
}

const OccurrenceDropdown = ({
  occurrences,
  selectedOccurrence,
  onSelect,
}: OccurrenceDropdownProps) => {
  const {handleOpen, ref, handleClose, open} = useDropDown();
  const [searchValue, setSearchValue] = useState('');
  const filteredOccurrences = useMemo(() => {
    const searchTerm = searchValue.trim().toLowerCase();
    if (!searchTerm) return occurrences;
    return occurrences.filter((item) =>
      item.label.toLowerCase().includes(searchTerm),
    );
  }, [occurrences, searchValue]);
  const selectedIndex = occurrences.findIndex(
    (item) => item.key === selectedOccurrence?.key,
  );
  const selectedLabel = selectedOccurrence
    ? formatSendOptionLabel(selectedOccurrence, selectedIndex === 0)
    : 'Not launched';

  return (
    <OccurrenceControl ref={ref}>
      <OccurrenceTrigger
        type="button"
        aria-label="Check-in occurrence"
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={!occurrences.length}
        onClick={handleOpen}>
        <OccurrenceTriggerContent>
          <OccurrenceDot
            aria-hidden="true"
            data-testid="occurrence-status-dot"
          />
          <OccurrenceLabel>{selectedLabel}</OccurrenceLabel>
        </OccurrenceTriggerContent>
        <OccurrenceChevron aria-hidden="true">
          <ChevronDownIcon />
        </OccurrenceChevron>
      </OccurrenceTrigger>

      {open && (
        <OccurrenceMenu>
          <SearchBox2
            placeholder="Search check-in sends"
            value={searchValue}
            inputStyle={{height: '40px', fontWeight: 400}}
            onChange={(event) => setSearchValue(event.target.value)}
          />
          <OccurrenceOptions role="listbox" aria-label="Check-in sends">
            {filteredOccurrences.length ? (
              filteredOccurrences.map((item) => (
                <DropdownItem
                  key={item.key}
                  role="option"
                  aria-selected={item.key === selectedOccurrence?.key}
                  onClick={() => {
                    onSelect(item.key);
                    setSearchValue('');
                    handleClose();
                  }}>
                  <Body2 kind="textBody">
                    {formatSendOptionLabel(
                      item,
                      item.key === occurrences[0]?.key,
                    )}
                  </Body2>
                </DropdownItem>
              ))
            ) : (
              <NoOccurrenceMatch kind="textBody">
                No sends found
              </NoOccurrenceMatch>
            )}
          </OccurrenceOptions>
        </OccurrenceMenu>
      )}
    </OccurrenceControl>
  );
};

const userName = (user?: ManagedUser) => {
  const name = `${user?.firstName || ''} ${user?.lastName || ''}`.trim();
  return name || user?.email || 'Unknown participant';
};

const formatParticipantStartTime = (sendTime?: string) => {
  if (!sendTime) return 'Starts later in their local time';
  const value = dayjs(`2000-01-01T${sendTime}`);
  return value.isValid()
    ? `Starts at ${value.format('h:mm A')} local time`
    : 'Starts later in their local time';
};

const getCheckinResponseData = (
  response?:
    | ISpecificCheckinsWithResponse
    | ISpecificCheckinsWithResponseData
    | false
    | null,
) => {
  if (!response) return null;
  return 'data' in response ? response.data : response;
};

const labelForId = (id: string, groups: any[], people: any[]) => {
  const group = groups.find((item) => item.id === id);
  if (group) return group.name || group.groupName || id;
  const person = people.find((item) => item.id === id);
  return person ? userName(person) : id;
};

const editableSection = (value: string | null): EditableCycleSection | null =>
  value === 'basics' || value === 'content' || value === 'cadence'
    ? value
    : null;

type CycleHeaderAction = 'pause' | 'resume' | 'archive' | 'restore';

const cycleHeaderActionCopy: Record<
  CycleHeaderAction,
  {title: string; confirmLabel: string}
> = {
  pause: {title: 'Pause cycle?', confirmLabel: 'Pause cycle'},
  resume: {title: 'Resume cycle?', confirmLabel: 'Resume cycle'},
  archive: {title: 'Archive cycle?', confirmLabel: 'Archive cycle'},
  restore: {title: 'Restore cycle?', confirmLabel: 'Restore cycle'},
};

export const CheckinCycleResults = observer(() => {
  const theme = useTheme();
  const {cycleId} = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const {
    authStore: {auth},
    groupStore: {allGroups, setAllGroups},
    usersStore: {users, allUsers},
  } = useStoreContext();
  const canManageCycles = canAdministerCheckins(auth.user.role);
  const workspaceId = auth.user.workspace.id;
  const searchParams = new URLSearchParams(location.search);
  const requestedTab = searchParams.get('tab');
  const tab: DetailTab =
    requestedTab === 'settings' && canManageCycles ? 'settings' : 'responses';
  const editSection =
    canManageCycles && tab === 'settings'
      ? editableSection(searchParams.get('edit'))
      : null;
  const [responseFilter, setResponseFilter] = useState<ResponseFilter>('all');
  const [selectedOccurrence, setSelectedOccurrence] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [
    cycleHeaderAction,
    setCycleHeaderAction,
  ] = useState<CycleHeaderAction | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const deleteInFlightRef = useRef(false);
  const [sendingReminderId, setSendingReminderId] = useState<string | null>(
    null,
  );
  const [isSendingBulkReminders, setIsSendingBulkReminders] = useState(false);
  const [reminderCooldowns, setReminderCooldowns] = useState<ReminderCooldowns>(
    {},
  );
  const [cooldownNow, setCooldownNow] = useState(Date.now());
  const [
    submissionToExcuse,
    setSubmissionToExcuse,
  ] = useState<ManagedSubmission | null>(null);
  const [isExcusing, setIsExcusing] = useState(false);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [isLoadingGroups, setIsLoadingGroups] = useState(false);
  const [audienceError, setAudienceError] = useState<string | null>(null);
  const groupLoadCycleRef = useRef<string | null>(null);
  const request = useMemo(() => new CompanyCheckinsRequestImpl(), []);
  const controller = useMemo(() => new CompanyCheckinsController(request), [
    request,
  ]);
  const reminderCooldownStorageKey = getReminderCooldownStorageKey(
    workspaceId,
    cycleId || 'unknown-cycle',
  );
  const baseFilter = getAdminSubmissionFilter(cycleId);

  const {data: primaryResponse, isLoading: loadingPrimary} = useQuery(
    ['cycle-submissions', workspaceId, cycleId, 'user'],
    () => controller.getListOfCheckins({...baseFilter, include: 'user'}),
    {
      enabled: canManageCycles && Boolean(cycleId),
      refetchOnWindowFocus: false,
    },
  );
  const {data: enrichmentResponse, isLoading: loadingEnrichment} = useQuery(
    ['cycle-submissions', workspaceId, cycleId, 'cycle'],
    () => controller.getListOfCheckins({...baseFilter, include: 'cycle'}),
    {
      enabled: canManageCycles && Boolean(cycleId),
      refetchOnWindowFocus: false,
    },
  );
  const {data: adminCycle, isLoading: loadingAdminCycle} = useQuery<
    ICheckinCycleDetail | false
  >(
    ['check-in-cycle', cycleId],
    () => controller.getCheckinCycle(cycleId as string),
    {enabled: canManageCycles && Boolean(cycleId), refetchOnWindowFocus: false},
  );

  const submissions = useMemo(
    () =>
      withoutPendingInvitationSubmissions(
        attachCycleEnrichment(
          getSubmissionRows(primaryResponse),
          getSubmissionRows(enrichmentResponse),
        ),
        allUsers,
      ),
    [allUsers, enrichmentResponse, primaryResponse],
  );
  const cycleSubmissions = useMemo(
    () => submissions.filter((row) => getSubmissionCycleId(row) === cycleId),
    [cycleId, submissions],
  );
  const cycle = adminCycle as ManagedCycle | false | undefined;
  const occurrences = useMemo(
    () =>
      cycle
        ? buildOccurrenceGroups(
            cycleSubmissions,
            cycle,
            auth.user.workspace?.timezone,
          )
        : [],
    [auth.user.workspace?.timezone, cycle, cycleSubmissions],
  );

  useEffect(() => {
    if (!selectedOccurrence && occurrences[0])
      setSelectedOccurrence(occurrences[0].key);
  }, [occurrences, selectedOccurrence]);
  useEffect(() => {
    if (!canManageCycles) navigate('/checkins/my-checkins', {replace: true});
  }, [canManageCycles, navigate]);

  useEffect(() => {
    const now = Date.now();
    setCooldownNow(now);
    setReminderCooldowns(
      readReminderCooldowns(reminderCooldownStorageKey, now),
    );
  }, [reminderCooldownStorageKey]);

  useEffect(() => {
    if (!Object.keys(reminderCooldowns).length) return;
    const interval = window.setInterval(() => {
      const now = Date.now();
      setCooldownNow(now);
      setReminderCooldowns((current) => {
        const active = Object.fromEntries(
          Object.entries(current).filter(([, expiresAt]) => expiresAt > now),
        );
        if (Object.keys(active).length !== Object.keys(current).length) {
          writeReminderCooldowns(reminderCooldownStorageKey, active);
          return active;
        }
        return current;
      });
    }, 1000);

    return () => window.clearInterval(interval);
  }, [reminderCooldownStorageKey, reminderCooldowns]);

  const groups = useMemo(
    () => Array.from(allGroups.values()) as any[],
    // Observable map contents can change without the map reference changing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allGroups, allGroups.size],
  );
  const groupOptions = useMemo<SelectOption[]>(
    () =>
      groups.map((group) => ({
        value: group.id,
        label:
          group.name ||
          group.groupName ||
          group.groupTypeName ||
          'Unnamed group',
      })),
    [groups],
  );
  const peopleOptions = useMemo<SelectOption[]>(
    () => users.map((user: any) => ({value: user.id, label: userName(user)})),
    [users],
  );
  const cycleGroupIds = useMemo(
    () =>
      cycle
        ? [...(cycle.includedGroups || []), ...(cycle.excludedGroups || [])]
        : [],
    [cycle],
  );
  const hasUnresolvedCycleGroups = cycleGroupIds.some(
    (groupId) =>
      !groupOptions.some((option) => option.value === String(groupId)),
  );

  const initGroups = useCallback(async () => {
    setIsLoadingGroups(true);
    setAudienceError(null);
    try {
      const groupTypes = await new groupTypeController(
        new groupTypeRequestImpl(),
      ).newfetchGroupType();
      if (!Array.isArray(groupTypes))
        throw new Error('Unable to load groups and teams.');
      const groupCtrl = new groupController(new groupRequestImpl());
      await Promise.all(
        groupTypes.map(async (groupType: any) => {
          const response: any = await groupCtrl.newfetchGroup(groupType.id);
          if (response?.groups) setAllGroups(response.groups, groupType.name);
        }),
      );
    } catch {
      setAudienceError('Unable to load groups and teams.');
    } finally {
      setIsLoadingGroups(false);
    }
  }, [setAllGroups]);

  useEffect(() => {
    const shouldLoadGroups =
      editSection === 'basics' || hasUnresolvedCycleGroups;
    if (
      !cycleId ||
      !shouldLoadGroups ||
      groupLoadCycleRef.current === cycleId
    ) {
      return;
    }

    groupLoadCycleRef.current = cycleId;
    initGroups();
  }, [cycleId, editSection, hasUnresolvedCycleGroups, initGroups]);

  const occurrence =
    occurrences.find((item) => item.key === selectedOccurrence) ||
    occurrences[0];
  const isLatestOccurrence = Boolean(
    occurrence && occurrences[0] && occurrence.key === occurrences[0].key,
  );
  const expectedAudience = useMemo(
    () => (cycle ? resolveCycleAudience(cycle, users, groups) : []),
    [cycle, groups, users],
  );
  const occurrenceSubmissions = useMemo(() => {
    const actualSubmissions = occurrence?.submissions || [];
    return cycle && isLatestOccurrence
      ? mergeAudienceWithOccurrence(actualSubmissions, expectedAudience, cycle)
      : actualSubmissions;
  }, [cycle, expectedAudience, isLatestOccurrence, occurrence]);
  const isAnonymousCycle = Boolean(cycle && cycle.visibility === 'anonymous');
  const stats = getParticipationStats(occurrenceSubmissions);
  const remindableSubmissions = useMemo(
    () =>
      isLatestOccurrence
        ? occurrenceSubmissions.filter(
            (row) =>
              Boolean(row.id) && !isSubmissionSubmitted(row) && !row.isExcused,
          )
        : [],
    [isLatestOccurrence, occurrenceSubmissions],
  );
  const availableReminderSubmissions = useMemo(
    () =>
      remindableSubmissions.filter(
        (row) =>
          !row.id ||
          getReminderSecondsRemaining(
            reminderCooldowns[row.id],
            cooldownNow,
          ) === 0,
      ),
    [cooldownNow, remindableSubmissions, reminderCooldowns],
  );
  const excusedCount = occurrenceSubmissions.filter((row) => row.isExcused)
    .length;
  const scheduledCount = occurrenceSubmissions.filter(
    (row) => row.availabilityState === 'scheduled',
  ).length;
  const visibleSubmissions = useMemo(
    () =>
      occurrenceSubmissions
        .filter(
          (row) =>
            responseFilter === 'all' ||
            (responseFilter === 'submitted'
              ? isSubmissionSubmitted(row) && !row.isExcused
              : !isSubmissionSubmitted(row) && !row.isExcused),
        )
        .sort((first, second) => {
          const rank = (row: typeof first) => {
            if (row.isExcused) return 3;
            if (isSubmissionSubmitted(row)) return 2;
            return row.availabilityState === 'scheduled' ? 1 : 0;
          };
          return rank(first) - rank(second);
        }),
    [occurrenceSubmissions, responseFilter],
  );
  const anonymousSubmittedSubmissions = useMemo(
    () => {
      if (!isAnonymousCycle) return [];
      const byRespondent = new Map<string, ManagedSubmission>();
      occurrenceSubmissions.forEach((submission) => {
        if (!submission.id || !isSubmissionSubmitted(submission)) return;
        const user = submission.user;
        const respondentId =
          typeof user === 'string' ? user : user?.id || user?.email;
        if (respondentId) byRespondent.set(respondentId, submission);
      });
      return Array.from(byRespondent.values());
    },
    [isAnonymousCycle, occurrenceSubmissions],
  );
  const anonymousResponseQueries = useQueries(
    (anonymousSubmittedSubmissions.length >= MIN_ANONYMOUS_PULSE_ANSWERS
      ? anonymousSubmittedSubmissions
      : []
    ).map((submission) => ({
      queryKey: [
        'cycle-anonymous-submission-response',
        workspaceId,
        cycleId,
        submission.id,
      ],
      queryFn: async () => {
        const response = await controller.getCheckinByIdAndFetchResponses(
          submission.id as string,
        );
        const detail = getCheckinResponseData(response);
        if (!detail) throw new Error('Unable to load anonymous response');
        return detail;
      },
      enabled: Boolean(submission.id),
      refetchOnWindowFocus: false,
      retry: false,
    })),
  ) as UseQueryResult<ISpecificCheckinsWithResponseData>[];
  const anonymousSummary = useMemo(
    () =>
      buildAnonymousPulseSummary(
        cycle || null,
        anonymousResponseQueries.map(
          (query) => query.data?.responses?.pulse?.mood,
        ),
      ),
    [anonymousResponseQueries, cycle],
  );
  const isLoadingAnonymousSummary = anonymousResponseQueries.some(
    (query) => query.isLoading,
  );
  const hasAnonymousSummaryError = anonymousResponseQueries.some(
    (query) => query.isError,
  );
  const status = cycle ? getEffectiveCycleStatus(cycle) : '';
  const isDraftCycle = Boolean(canManageCycles && cycle && status === 'draft');

  useEffect(() => {
    if (!isDraftCycle || !cycleId) return;
    navigate(
      `/create-checkin-cycle?cycleId=${encodeURIComponent(
        cycleId,
      )}&preview=true`,
      {replace: true},
    );
  }, [cycleId, isDraftCycle, navigate]);

  const navigateWithParams = (next: {
    tab?: DetailTab;
    edit?: EditableCycleSection | null;
  }) => {
    const params = new URLSearchParams(location.search);
    if (next.tab) params.set('tab', next.tab);
    if (next.edit) params.set('edit', next.edit);
    else params.delete('edit');
    navigate(
      `/checkins/team-management/cycles/${cycleId}?${params.toString()}`,
      {replace: true},
    );
  };

  const setTab = (next: DetailTab) =>
    navigateWithParams({tab: next, edit: null});

  const confirmCycleHeaderAction = async () => {
    if (!cycleId || !cycleHeaderAction || actionLoading) return;
    const data = {
      pause: {isPaused: true},
      resume: {isPaused: false},
      archive: {status: 'archived'},
      restore: {status: 'ended'},
    }[cycleHeaderAction];
    setActionLoading(true);
    try {
      const result = await controller.updateCheckinCycle(cycleId, data);
      if (result !== false) {
        await Promise.all([
          queryClient.invalidateQueries(['check-in-cycles']),
          queryClient.invalidateQueries(['check-in-cycle', cycleId]),
          queryClient.invalidateQueries(['cycle-submissions', workspaceId]),
        ]);
        setCycleHeaderAction(null);
      }
    } finally {
      setActionLoading(false);
    }
  };

  const deleteCycle = async () => {
    if (!cycleId || deleteInFlightRef.current) return;
    deleteInFlightRef.current = true;
    setActionLoading(true);
    try {
      const result = await controller.deleteCheckinCycle(cycleId);
      if (result === false) return;
      setDeleteModalOpen(false);
      queryClient.removeQueries(['check-in-cycle', cycleId]);
      await Promise.all([
        queryClient.invalidateQueries(['check-in-cycles']),
        queryClient.invalidateQueries(['cycle-submissions', workspaceId]),
      ]);
      navigate('/checkins/team-management', {replace: true});
    } finally {
      deleteInFlightRef.current = false;
      setActionLoading(false);
    }
  };

  const saveCycleSection = async (
    data: ReturnType<typeof mapCycleToFormData>,
  ) => {
    if (!cycleId || !cycle) return false;
    setIsSavingEdit(true);
    try {
      const result = await controller.updateCheckinCycle(
        cycleId,
        buildPayload(data, cycle.status || 'active'),
      );
      if (!result) return false;
      await Promise.all([
        queryClient.invalidateQueries(['check-in-cycles']),
        queryClient.invalidateQueries(['check-in-cycle', cycleId]),
        queryClient.invalidateQueries(['cycle-submissions', workspaceId]),
      ]);
      navigateWithParams({tab: 'settings', edit: null});
      return true;
    } finally {
      setIsSavingEdit(false);
    }
  };

  const startReminderCooldown = (submissionIds: string[]) => {
    if (!submissionIds.length) return;
    const now = Date.now();
    const expiresAt = now + REMINDER_COOLDOWN_MS;
    setCooldownNow(now);
    setReminderCooldowns((current) => {
      const next = {...current};
      submissionIds.forEach((submissionId) => {
        next[submissionId] = expiresAt;
      });
      writeReminderCooldowns(reminderCooldownStorageKey, next);
      return next;
    });
  };

  const sendReminder = async (submissionId: string) => {
    if (
      getReminderSecondsRemaining(
        reminderCooldowns[submissionId],
        cooldownNow,
      ) > 0
    )
      return;
    setSendingReminderId(submissionId);
    try {
      const result = await controller.sendCheckinReminder(submissionId);
      if (result !== false) startReminderCooldown([submissionId]);
    } finally {
      setSendingReminderId(null);
    }
  };

  const sendBulkReminders = async () => {
    const reminderIds = availableReminderSubmissions
      .map((submission) => submission.id)
      .filter((id): id is string => Boolean(id));
    if (!reminderIds.length) return;
    setIsSendingBulkReminders(true);
    try {
      const result = await controller.sendCheckinReminders(reminderIds);
      const successfulIds = result?.successfulIds?.length
        ? result.successfulIds
        : result?.failedCount === 0
        ? reminderIds
        : [];
      startReminderCooldown(successfulIds);
    } finally {
      setIsSendingBulkReminders(false);
    }
  };

  const markSubmissionAsExcused = async () => {
    if (!submissionToExcuse?.id) return;
    setIsExcusing(true);
    try {
      const result = await controller.updateCheckinById(
        {isExcused: true},
        submissionToExcuse.id,
      );
      if (!result) return;

      const submissionId = submissionToExcuse.id;
      queryClient.setQueryData(
        ['cycle-submissions', workspaceId, cycleId, 'user'],
        (current) => markSubmissionExcusedInList(current as any, submissionId),
      );
      queryClient.setQueryData(
        ['cycle-submissions', workspaceId, cycleId, 'cycle'],
        (current) => markSubmissionExcusedInList(current as any, submissionId),
      );
      await queryClient.invalidateQueries([
        'cycle-submissions',
        workspaceId,
        cycleId,
      ]);
      setSubmissionToExcuse(null);
    } finally {
      setIsExcusing(false);
    }
  };

  const pageBodyStyle = {
    minHeight: 'calc(100vh - 65px)',
    backgroundColor: theme.colors.backgroundLight,
  };

  const headerActions = () => {
    if (!canManageCycles || !cycle) return null;
    if (status === 'draft') return null;
    if (status === 'archived') {
      return (
        <CycleActionButton
          kind="secondary"
          disabled={actionLoading}
          onClick={() => setCycleHeaderAction('restore')}>
          Restore
        </CycleActionButton>
      );
    }
    if (status === 'ended') {
      return (
        <CycleActionButton
          kind="secondary"
          disabled={actionLoading}
          onClick={() => setCycleHeaderAction('archive')}>
          Archive
        </CycleActionButton>
      );
    }
    return (
      <CycleActionButton
        kind="secondary"
        disabled={actionLoading}
        onClick={() =>
          setCycleHeaderAction(status === 'paused' ? 'resume' : 'pause')
        }>
        {status === 'paused' ? 'Resume Check-in' : 'Pause Check-in'}
      </CycleActionButton>
    );
  };

  const included = cycle
    ? !cycle.includedGroups?.length && !cycle.includedIndividuals?.length
      ? ['Everyone']
      : [
          ...(cycle.includedGroups || []),
          ...(cycle.includedIndividuals || []),
        ].map((id) => labelForId(id, groups, users))
    : [];
  const excluded = cycle
    ? [
        ...(cycle.excludedGroups || []),
        ...(cycle.excludedIndividuals || []),
      ].map((id) => labelForId(id, groups, users))
    : [];
  const formData = useMemo(
    () =>
      cycle && canManageCycles
        ? mapCycleToFormData(
            cycle as ICheckinCycleDetail,
            groupOptions,
            peopleOptions,
          )
        : null,
    [canManageCycles, cycle, groupOptions, peopleOptions],
  );
  const loading =
    loadingPrimary ||
    loadingEnrichment ||
    (canManageCycles && loadingAdminCycle);

  if (loading || isDraftCycle) {
    return (
      <PageLayoutTemplate
        title="View Cycle"
        onClick={() => navigate('/checkins/team-management')}
        bodyStyle={pageBodyStyle}>
        <Empty>
          <ItemLoader />
        </Empty>
      </PageLayoutTemplate>
    );
  }

  if (!cycle) {
    return (
      <PageLayoutTemplate
        title="View Cycle"
        onClick={() => navigate('/checkins/team-management')}
        bodyStyle={pageBodyStyle}>
        <Empty>This cycle is not available in your Team Management list.</Empty>
      </PageLayoutTemplate>
    );
  }

  return (
    <PageLayoutTemplate
      title="View Cycle"
      onClick={() => navigate('/checkins/team-management')}
      bodyStyle={pageBodyStyle}>
      <Page>
        <CycleDetailCard data-testid="cycle-detail-shell">
          <CycleHeader data-testid="cycle-detail-header">
            <CycleIdentity>
              <CycleName>{cycle.cycleName}</CycleName>
              <CycleStatus $active={status === 'active'}>{status}</CycleStatus>
            </CycleIdentity>
            <Actions>{headerActions()}</Actions>
          </CycleHeader>

          <Tabs>
            <Tab
              $active={tab === 'responses'}
              onClick={() => setTab('responses')}>
              Responses
            </Tab>
            {canManageCycles && status !== 'archived' && (
              <Tab
                $active={tab === 'settings'}
                onClick={() => setTab('settings')}>
                Settings
              </Tab>
            )}
          </Tabs>

          {tab === 'responses' ? (
            <ResponsesContent>
              <SendCycleRow>
                <OccurrencePicker>
                  Send cycle
                  <OccurrenceDropdown
                    occurrences={occurrences}
                    selectedOccurrence={occurrence}
                    onSelect={setSelectedOccurrence}
                  />
                </OccurrencePicker>
              </SendCycleRow>

              <ParticipationSummary data-testid="participation-summary">
                <ParticipationHeader>
                  <ParticipationCopy>
                    <strong>
                      {stats.submittedCount} of {stats.totalCount} submitted
                    </strong>
                    <span>
                      {scheduledCount
                        ? `${scheduledCount} scheduled · ${stats.percentage}% overall participation`
                        : `${stats.percentage}% participation`}
                      {excusedCount ? ` · ${excusedCount} excused` : ''}
                    </span>
                  </ParticipationCopy>
                  <BulkReminderButton
                    disabled={
                      !availableReminderSubmissions.length ||
                      isSendingBulkReminders ||
                      Boolean(sendingReminderId)
                    }
                    isLoading={isSendingBulkReminders}
                    onClick={sendBulkReminders}>
                    Remind all pending ({availableReminderSubmissions.length})
                  </BulkReminderButton>
                </ParticipationHeader>
                <ProgressTrack
                  role="progressbar"
                  aria-label="Cycle participation"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={stats.percentage}>
                  <ProgressValue style={{width: `${stats.percentage}%`}} />
                </ProgressTrack>
              </ParticipationSummary>

              {isAnonymousCycle ? (
                <AnonymousPulseSummary
                  summary={anonymousSummary}
                  hasSubmittedResponses={stats.submittedCount > 0}
                  isLoading={isLoadingAnonymousSummary}
                  isError={hasAnonymousSummaryError}
                  onRetry={() => {
                    anonymousResponseQueries.forEach((query) =>
                      query.refetch(),
                    );
                  }}
                />
              ) : (
                <ParticipantsCard>
                  <ParticipantsToolbar>
                    <Filters aria-label="Filter responses">
                      <Filter
                        $active={responseFilter === 'all'}
                        onClick={() => setResponseFilter('all')}>
                        All
                      </Filter>
                      <Filter
                        $active={responseFilter === 'submitted'}
                        onClick={() => setResponseFilter('submitted')}>
                        Submitted
                      </Filter>
                      <Filter
                        $active={responseFilter === 'pending'}
                        onClick={() => setResponseFilter('pending')}>
                        Not Submitted
                      </Filter>
                    </Filters>
                    <SortLabel>Sorted: pending first</SortLabel>
                  </ParticipantsToolbar>

                  <Participants>
                    {visibleSubmissions.length ? (
                      visibleSubmissions.map((submission, index) => {
                        const user =
                          typeof submission.user === 'object'
                            ? submission.user
                            : undefined;
                        const submissionStatus = getCheckinSubmissionStatus(
                          submission,
                          {isLatestOccurrence},
                        );
                        const {kind} = submissionStatus;
                        const excused = kind === 'excused';
                        const submitted = kind === 'submitted';
                        const scheduled = kind === 'scheduled';
                        const submissionId = submission.id;
                        const reminderSeconds = submissionId
                          ? getReminderSecondsRemaining(
                              reminderCooldowns[submissionId],
                              cooldownNow,
                            )
                          : 0;
                        const timestamp = getSubmittedAt(submission);
                        const participantTone =
                          kind === 'excused'
                            ? 'muted'
                            : kind === 'submitted'
                            ? 'submitted'
                            : kind === 'scheduled' || kind === 'pending'
                            ? 'muted'
                            : 'pending';
                        const participantStatus = checkinSubmissionStatusLabel(
                          submissionStatus,
                        );
                        const rowPosition =
                          visibleSubmissions.length === 1
                            ? 'only'
                            : index === 0
                            ? 'first'
                            : index === visibleSubmissions.length - 1
                            ? 'last'
                            : 'middle';
                        return (
                          <Participant
                            $position={rowPosition}
                            data-testid={`participant-row-${index}`}
                            key={
                              submissionId ||
                              `${user?.id}-${submission.createdAt}`
                            }>
                            <Person>
                              <Avatar
                                tooltip={true}
                                userId={user?.id || ''}
                                name={userName(user)}
                                size="md"
                                timezone={user?.timezone}
                                src={user?.avatar?.url}
                              />
                              <PersonDetails>
                                <strong>{userName(user)}</strong>
                                <span>{user?.jobTitle || '—'}</span>
                              </PersonDetails>
                            </Person>
                            <Timestamp>
                              {timestamp
                                ? dayjs(timestamp).format('MMM D, h:mm A')
                                : scheduled
                                ? formatParticipantStartTime(
                                    cycle.config?.sendTime,
                                  )
                                : '—'}
                            </Timestamp>
                            <ParticipantStatus
                              $tone={participantTone}
                              data-tone={participantTone}
                              data-testid={`participant-status-${
                                submissionId || user?.id
                              }`}>
                              {participantStatus}
                            </ParticipantStatus>
                            <ActionCell>
                              {!excused && submitted && submissionId ? (
                                <ViewButton
                                  kind="secondary"
                                  onClick={() => {
                                    const detailParams = new URLSearchParams();
                                    if (user?.id)
                                      detailParams.set('user', user.id);
                                    if (occurrence?.key)
                                      detailParams.set('send', occurrence.key);
                                    detailParams.set(
                                      'returnTo',
                                      `${location.pathname}${location.search}`,
                                    );
                                    navigate(
                                      `/checkins/team-management/cycles/${cycleId}/participants/${
                                        user?.id
                                      }?${detailParams.toString()}`,
                                      {
                                        state: {
                                          user: user?.id,
                                          returnTo: `${location.pathname}${location.search}`,
                                        },
                                      },
                                    );
                                  }}>
                                  View
                                </ViewButton>
                              ) : submissionId &&
                                !excused &&
                                isLatestOccurrence ? (
                                <ReminderButton
                                  data-cooling={reminderSeconds > 0}
                                  disabled={
                                    sendingReminderId === submissionId ||
                                    isSendingBulkReminders ||
                                    reminderSeconds > 0
                                  }
                                  isLoading={sendingReminderId === submissionId}
                                  onClick={() => sendReminder(submissionId)}>
                                  {reminderSeconds > 0
                                    ? `Remind again in 0:${String(
                                        reminderSeconds,
                                      ).padStart(2, '0')}`
                                    : 'Remind'}
                                </ReminderButton>
                              ) : null}
                              {submissionId && (
                                <DropdownVertical
                                  buttonAriaLabel={`Actions for ${userName(
                                    user,
                                  )}`}
                                  collapseOnClick={true}
                                  disabled={excused}
                                  customIcon={
                                    <MoreVerticalIcon2
                                      fill={
                                        excused
                                          ? theme.colors.textMuted
                                          : theme.colors.textBody
                                      }
                                      stroke={
                                        excused
                                          ? theme.colors.textMuted
                                          : theme.colors.textBody
                                      }
                                    />
                                  }
                                  dropdownWrapperStyle={{
                                    right: 0,
                                    minWidth: 269,
                                    maxWidth: 269,
                                    padding: 8,
                                    border: 'none',
                                    borderRadius: 7,
                                    boxShadow:
                                      '0 2px 5px rgba(172, 172, 190, 0.24)',
                                  }}
                                  menu={(handleClose: () => void) => (
                                    <ExcuseMenu>
                                      <ExcuseMenuItem
                                        onClick={() => {
                                          setSubmissionToExcuse(submission);
                                          handleClose();
                                        }}>
                                        Mark as excused
                                      </ExcuseMenuItem>
                                    </ExcuseMenu>
                                  )}
                                />
                              )}
                            </ActionCell>
                          </Participant>
                        );
                      })
                    ) : (
                      <ParticipantsEmpty>
                        {occurrences.length
                          ? 'No participants match this filter.'
                          : 'This cycle has not launched yet.'}
                      </ParticipantsEmpty>
                    )}
                  </Participants>
                </ParticipantsCard>
              )}
            </ResponsesContent>
          ) : (
            <Settings data-testid="cycle-detail-settings">
              <PreviewParticipantsCard
                cycleName={cycle.cycleName}
                includedTags={included}
                excludedTags={excluded}
                cardVariant="detail"
                onEdit={() =>
                  navigateWithParams({tab: 'settings', edit: 'basics'})
                }
              />
              <PreviewContentCard
                blocks={buildBlocksFromCycle(cycle as ICheckinCycleDetail)}
                visibility={cycle.visibility || 'private'}
                cardVariant="detail"
                onEdit={() =>
                  navigateWithParams({tab: 'settings', edit: 'content'})
                }
              />
              <PreviewCadenceCard
                cardVariant="detail"
                details={
                  formData
                    ? formatCadenceDetails(formData.cadence)
                    : {
                        frequencyLabel: '—',
                        startLabel: '—',
                        sendOnLabel: '',
                        endLabel: '—',
                        timeLabel: '',
                      }
                }
                onEdit={() =>
                  navigateWithParams({tab: 'settings', edit: 'cadence'})
                }
              />

              <CycleActions>
                <div>
                  <Body1 weight="semibold">Danger Zone</Body1>
                  <Body2 kind="textBody">
                    Once you delete a check-in, there is no going back. Please
                    be certain.
                  </Body2>
                </div>
                <Button
                  kind="secondary"
                  disabled={actionLoading}
                  onClick={() => setDeleteModalOpen(true)}>
                  Delete this check-in
                </Button>
              </CycleActions>
            </Settings>
          )}
        </CycleDetailCard>
      </Page>

      {editSection && formData && status !== 'archived' && (
        <CycleEditSidecar
          section={editSection}
          data={formData}
          groupOptions={groupOptions}
          peopleOptions={peopleOptions}
          isLoadingGroups={isLoadingGroups}
          audienceError={audienceError}
          onRetryAudience={initGroups}
          cycleStatus={status}
          isSaving={isSavingEdit}
          onClose={() => navigateWithParams({tab: 'settings', edit: null})}
          onSave={saveCycleSection}
        />
      )}

      {submissionToExcuse && (
        <Modal open={true} onClose={() => setSubmissionToExcuse(null)}>
          <div style={{maxWidth: '500px', margin: 'auto'}}>
            <div className="flex w-full justify-end">
              <BackAction
                width="md"
                icon={<CancelIcon />}
                onClick={() => setSubmissionToExcuse(null)}
                title="Close"
              />
            </div>
            <VerticalSpacer size="16px" />
            <ModalCard align="center" title="">
              <div>
                <VerticalSpacer size="16px" />
                <Headline2 align="center">
                  Mark{' '}
                  {userName(
                    typeof submissionToExcuse.user === 'object'
                      ? submissionToExcuse.user
                      : undefined,
                  )}{' '}
                  as excused
                </Headline2>
                <VerticalSpacer size="16px" />
                <Body1 kind="textBody" align="center">
                  This check-in will be marked as excused and removed from the
                  participation total. You cannot undo this action.
                </Body1>
                <VerticalSpacer size="16px" />
                <Button
                  width="full"
                  disabled={isExcusing}
                  isLoading={isExcusing}
                  onClick={markSubmissionAsExcused}>
                  Confirm
                </Button>
              </div>
            </ModalCard>
          </div>
        </Modal>
      )}

      {cycleHeaderAction && (
        <ConfirmationDialog
          open
          title={cycleHeaderActionCopy[cycleHeaderAction].title}
          confirmLabel={cycleHeaderActionCopy[cycleHeaderAction].confirmLabel}
          destructive={cycleHeaderAction === 'archive'}
          isLoading={actionLoading}
          onCancel={() => {
            if (!actionLoading) setCycleHeaderAction(null);
          }}
          onConfirm={confirmCycleHeaderAction}>
          {cycleHeaderAction === 'pause' && (
            <>
              Pausing <strong>{cycle.cycleName}</strong> will stop new
              occurrences until the cycle is resumed.
            </>
          )}
          {cycleHeaderAction === 'resume' && (
            <>
              Resuming <strong>{cycle.cycleName}</strong> will allow new
              occurrences again.
            </>
          )}
          {cycleHeaderAction === 'archive' && (
            <>
              Archiving <strong>{cycle.cycleName}</strong> will remove it from
              the inactive cycle list. You can restore it later.
            </>
          )}
          {cycleHeaderAction === 'restore' && (
            <>
              Restoring <strong>{cycle.cycleName}</strong> will return it to the
              inactive cycle list.
            </>
          )}
        </ConfirmationDialog>
      )}

      <ConfirmationDialog
        open={deleteModalOpen}
        title="Delete check-in?"
        confirmLabel="Delete check-in"
        isLoading={actionLoading}
        onCancel={() => {
          if (!actionLoading) setDeleteModalOpen(false);
        }}
        onConfirm={deleteCycle}>
        Deleting <strong>{cycle.cycleName}</strong> cannot be undone.
      </ConfirmationDialog>
    </PageLayoutTemplate>
  );
});
